import React, { useEffect } from 'react';
import GlobalHeader from '../components/GlobalHeader';
import GlobalFooter from '../components/GlobalFooter';
import LegalHero from '../components/LegalHero';
import PageSEO from '../components/PageSEO';

export default function TermsOfService() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      window.scrollTo({
        top: el.offsetTop - 100,
        behavior: 'smooth'
      });
    }
  };

  const navItems = [
    { id: 'sec-1', label: '1. Agreement' },
    { id: 'sec-2', label: '2. Eligibility' },
    { id: 'sec-3', label: '3. What the Service Does' },
    { id: 'sec-4', label: '4. Accounts' },
    { id: 'sec-5', label: '5. Free Audits and Trials' },
    { id: 'sec-6', label: '6. Plans, Fees, and Payment' },
    { id: 'sec-7', label: '7. Your Responsibilities' },
    { id: 'sec-8', label: '8. Acceptable Use' },
    { id: 'sec-9', label: '9. Intellectual Property' },
    { id: 'sec-10', label: '10. Third-Party Services' },
    { id: 'sec-11', label: '11. Confidentiality' },
    { id: 'sec-12', label: '12. Privacy' },
    { id: 'sec-13', label: '13. Disclaimers' },
    { id: 'sec-14', label: '14. Limitation of Liability' },
    { id: 'sec-15', label: '15. Indemnification' },
    { id: 'sec-16', label: '16. Term and Termination' },
    { id: 'sec-17', label: '17. Governing Law & Disputes' },
    { id: 'sec-18', label: '18. General' },
    { id: 'sec-19', label: '19. Contact' },
  ];

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": "https://aisearchadvisers.com/#organization",
        "name": "AI Search Advisers",
        "url": "https://aisearchadvisers.com/",
        "description": "AI Search Advisers helps businesses assess and improve website readiness for AI-driven discovery through website audits, AI visibility insights, crawlability analysis, structured data evaluation, and prioritized recommendations.",
        "email": "info@aisearchadvisers.com"
      },
      {
        "@type": "WebSite",
        "@id": "https://aisearchadvisers.com/#website",
        "url": "https://aisearchadvisers.com/",
        "name": "AI Search Advisers",
        "publisher": { "@id": "https://aisearchadvisers.com/#organization" },
        "inLanguage": "en-US"
      },
      {
        "@type": "WebPage",
        "@id": "https://aisearchadvisers.com/terms-of-service#webpage",
        "url": "https://aisearchadvisers.com/terms-of-service",
        "name": "Terms of Service | AI Search Advisers",
        "description": "Review the AI Search Advisers Terms of Service covering website access, account usage, services, responsibilities, and other applicable terms.",
        "isPartOf": { "@id": "https://aisearchadvisers.com/#website" },
        "about": { "@id": "https://aisearchadvisers.com/#organization" },
        "publisher": { "@id": "https://aisearchadvisers.com/#organization" },
        "inLanguage": "en-US"
      }
    ]
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2] font-sans text-[#021426] flex flex-col selection:bg-[#FEAB3D]/30 selection:text-[#021426]">
      <PageSEO 
        title="Terms of Service | AI Search Advisers"
        description="Review the AI Search Advisers Terms of Service covering website access, account usage, services, responsibilities, and other applicable terms."
        canonical="https://aisearchadvisers.com/terms-of-service"
        schema={schema}
      />
      <GlobalHeader />

      <LegalHero 
        eyebrow="LEGAL"
        title="Terms of Service"
        supportingText="AI Search Advisers"
        effectiveDate="September 21, 2026"
        lastUpdated="September 21, 2026"
      />

      {/* CONTENT & SIDEBAR */}
      <section className="px-6 py-16 relative z-20">
        <div className="max-w-[1100px] mx-auto flex flex-col lg:flex-row gap-12 lg:gap-20 items-start">
          
          {/* SIDEBAR NAVIGATION (Desktop) */}
          <div className="hidden lg:block w-64 shrink-0 sticky top-32">
            <h4 className="font-['Fraunces',serif] text-lg font-bold text-[#021426] mb-4">On this page</h4>
            <div className="flex flex-col gap-3 border-l-2 border-[#e5e9ee] pl-4">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className="text-left text-sm text-[#4C545B] hover:text-[#0C5B94] hover:translate-x-1 transition-all font-medium"
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          {/* MAIN CONTENT */}
          <div className="flex-1 max-w-[800px] legal-content w-full">

            <p>These Terms of Service ("Terms") govern your use of the AI Search Advisers website, software, reports, audits, analytics, and related services (collectively, the "Service").</p>
            <p>By creating an account, running an audit, subscribing to a paid plan, or otherwise using the Service, you agree to these Terms.</p>
            <p>If you do not agree to these Terms, you should not use the Service.</p>
            <p>If you use the Service on behalf of a company or other organization, you confirm that you have authority to accept these Terms on its behalf.</p>

            <h2 id="sec-1">1. Agreement</h2>
            <p>These Terms form a binding agreement between you and AI Search Advisers regarding your access to and use of the Service.</p>
            <p>They apply to all website visitors, account holders, subscribers, customers, and organizations using the Service.</p>
            <p>Certain services, including custom Done For You engagements, may also be subject to separate written agreements or statements of work.</p>
            <p>Where a separate written agreement expressly conflicts with these Terms, the separate written agreement will control for that specific engagement.</p>

            <h2 id="sec-2">2. Eligibility</h2>
            <p>You must be at least 18 years old and legally capable of entering into a binding agreement to use the Service.</p>
            <p>AI Search Advisers is intended primarily for business and professional use.</p>
            <p>If you use the Service on behalf of an organization, you confirm that you are authorized to act on its behalf.</p>

            <h2 id="sec-3">3. What the Service Does — and What It Does Not Do</h2>
            <p>AI Search Advisers analyzes publicly available information about websites and businesses and provides reports relating to visibility in AI-powered search and discovery systems.</p>
            <p>Depending on your plan, the Service may also include monitoring, recommendations, technical improvements, content work, business listing improvements, citation work, or other implementation services.</p>
            <p>You acknowledge and agree that:</p>
            <ul>
              <li>AI Search Advisers does not control, operate, or determine the behavior of third-party AI systems, search engines, directories, or platforms, including ChatGPT, Perplexity, Gemini, Claude, Google, or similar services.</li>
              <li>We do not guarantee that your business will be mentioned, recommended, cited, displayed, or ranked by any AI system, search engine, or directory.</li>
              <li>We do not guarantee any particular increase in traffic, revenue, rankings, visibility, leads, or other business results.</li>
              <li>AI systems are non-deterministic and may provide different answers to the same or similar questions at different times.</li>
              <li>Results may vary according to user location, model version, platform changes, available information, and other factors outside our control.</li>
              <li>Reports describe signals and observations available at a particular point in time.</li>
              <li>Reports are informational and should not be treated as a certification, warranty, formal audit opinion, or professional legal, financial, tax, or regulatory advice.</li>
              <li>Third-party platforms may change their systems, policies, availability, pricing, or behavior at any time.</li>
            </ul>
            <p>Any statements we make about typical or observed results describe past or general experience and are not guarantees of future performance.</p>

            <h2 id="sec-4">4. Accounts</h2>
            <p>Where an account is required, you are responsible for:</p>
            <ul>
              <li>Providing accurate and current account information.</li>
              <li>Keeping your login credentials confidential.</li>
              <li>Maintaining reasonable security over your account.</li>
              <li>All activity conducted through your account.</li>
            </ul>
            <p>You should notify AI Search Advisers promptly if you believe your account has been accessed or used without authorization.</p>
            <p>We may suspend, restrict, refuse, or terminate account access where reasonably necessary, including where:</p>
            <ul>
              <li>These Terms are violated.</li>
              <li>Payment obligations are not met.</li>
              <li>The Service is being abused.</li>
              <li>Activity creates security, legal, or operational risk.</li>
              <li>Fraudulent or unauthorized activity is suspected.</li>
            </ul>

            <h2 id="sec-5">5. Free Audits and Trials</h2>
            <p>AI Search Advisers may offer free audits, free features, demonstrations, or trial access.</p>
            <p>Free services may be:</p>
            <ul>
              <li>Limited in scope.</li>
              <li>Limited in frequency.</li>
              <li>Subject to usage limits.</li>
              <li>Modified or discontinued at any time.</li>
              <li>Subject to anti-abuse controls.</li>
            </ul>
            <p>Automated, bulk, scraping, resale, or abusive use of free services is not permitted unless AI Search Advisers has provided written authorization.</p>
            <p>A free audit does not create an obligation to purchase a paid service.</p>

            <h2 id="sec-6">6. Plans, Fees, and Payment</h2>
            
            <h3>Pricing</h3>
            <p>Current pricing is displayed on the AI Search Advisers pricing page.</p>
            <p>Unless otherwise stated, prices are shown in U.S. dollars and do not include applicable taxes.</p>
            
            <h3>Billing</h3>
            <p>Paid subscription plans may be billed monthly or annually, depending on the plan selected.</p>
            <p>Subscription fees are generally charged in advance.</p>
            <p>Subscriptions automatically renew at the applicable billing interval until cancelled, unless otherwise stated.</p>
            <p>By purchasing a subscription, you authorize AI Search Advisers and its payment processor to charge the payment method associated with your account for applicable subscription fees, taxes, and other authorized charges.</p>
            
            <h3>Taxes</h3>
            <p>You are responsible for applicable sales tax, VAT, GST, use tax, or similar taxes unless the law requires us to collect and remit them directly.</p>
            
            <h3>Price Changes</h3>
            <p>We may change plan pricing, features, or billing structures from time to time.</p>
            <p>Where required and reasonably practicable, existing subscribers will receive advance notice of material pricing changes before the new pricing applies to a future renewal.</p>
            
            <h3>Failed Payments</h3>
            <p>If a payment cannot be processed, we may:</p>
            <ul>
              <li>Retry the payment.</li>
              <li>Request an updated payment method.</li>
              <li>Restrict access to paid features.</li>
              <li>Suspend the subscription.</li>
              <li>Terminate the subscription where payment remains outstanding.</li>
            </ul>
            <p>You remain responsible for valid charges incurred before cancellation or termination.</p>
            
            <h3>Cancellation</h3>
            <p>Monthly subscriptions may be cancelled at any time.</p>
            <p>Cancellation generally becomes effective at the end of the current paid billing period, and access to paid features may continue until that period ends.</p>
            <p>Unless required by applicable law or separately agreed in writing, cancellation does not result in a refund for time already billed.</p>
            
            <h3>Refunds</h3>
            <p>Except where required by applicable law or expressly stated in a separate offer or agreement, fees are non-refundable.</p>
            <p>This includes unused features and partially used billing periods.</p>
            
            <h3>Done For You and Project Work</h3>
            <p>Custom implementation, consulting, agency, reseller, and Done For You services may be governed by a separate proposal, statement of work, service agreement, or similar written document.</p>
            <p>Where such a document conflicts with these Terms regarding that engagement, the specific written agreement will control.</p>

            <h2 id="sec-7">7. Your Responsibilities and Authority</h2>
            <p>You represent and confirm that:</p>
            <ul>
              <li>You own or are authorized to act on behalf of the owner of each website or domain submitted for analysis or remediation.</li>
              <li>You will not submit websites or domains for improper, unlawful, deceptive, abusive, or harassing purposes.</li>
              <li>You will not use the Service to obtain unauthorized access to systems, accounts, websites, or information.</li>
              <li>You have provided any notices and obtained any consents required for information you ask us to process.</li>
              <li>Your use of the Service complies with applicable laws and regulations.</li>
            </ul>
            <p>Where AI Search Advisers implements changes on your behalf, you are responsible for providing appropriate access and permissions.</p>
            <p>You are also responsible for maintaining appropriate backups of your own websites, systems, and content.</p>

            <h2 id="sec-8">8. Acceptable Use</h2>
            <p>You may not use the Service to:</p>
            <ul>
              <li>Reverse engineer, decompile, disassemble, or attempt to derive source code from the Service except where applicable law expressly permits it.</li>
              <li>Circumvent authentication, rate limits, security controls, or access restrictions.</li>
              <li>Interfere with the security, reliability, operation, or availability of the Service.</li>
              <li>Resell, sublicense, white-label, or provide the Service to third parties unless permitted under a written agreement with AI Search Advisers.</li>
              <li>Use automated systems to access the Service beyond permitted usage limits.</li>
              <li>Upload or distribute malware, malicious code, or unlawful material.</li>
              <li>Use the Service to violate another person's intellectual property, privacy, contractual, or legal rights.</li>
              <li>Use the Service to build or train a competing product or machine learning system using proprietary AI Search Advisers materials or functionality.</li>
              <li>Misrepresent an AI Search Advisers report as an endorsement, certification, guarantee, or statement made by ChatGPT, Google, Perplexity, Gemini, Claude, or another third-party platform.</li>
            </ul>
            <p>We may take reasonable action to prevent or respond to prohibited activity.</p>

            <h2 id="sec-9">9. Intellectual Property</h2>
            
            <h3>AI Search Advisers Materials</h3>
            <p>AI Search Advisers and its licensors retain ownership of the Service and related intellectual property, including:</p>
            <ul>
              <li>Software</li>
              <li>Methodologies</li>
              <li>Scoring systems</li>
              <li>Designs</li>
              <li>Templates</li>
              <li>Documentation</li>
              <li>Branding</li>
              <li>Trademarks</li>
              <li>Internal tools and processes</li>
            </ul>
            <p>Subject to compliance with these Terms and payment of applicable fees, you receive a limited, non-exclusive, non-transferable right to use the Service for your own authorized business purposes.</p>
            
            <h3>Your Content and Data</h3>
            <p>You retain ownership of your website, content, business information, and data.</p>
            <p>You grant AI Search Advisers the limited rights reasonably necessary to process that information in order to provide, maintain, secure, and improve the Service.</p>
            
            <h3>Reports</h3>
            <p>Reports generated for you may be used and shared for legitimate internal business purposes and with your professional advisers.</p>
            <p>You may not falsely attribute reports to another organization, remove required attribution, or resell reports except where a separate written agreement permits it.</p>
            
            <h3>Paid Deliverables</h3>
            <p>Custom content or code created specifically for you under a paid service may become yours once applicable fees have been fully paid, subject to the terms of the relevant engagement.</p>
            <p>AI Search Advisers retains ownership of its pre-existing tools, templates, methods, systems, libraries, and general know-how.</p>
            
            <h3>Feedback</h3>
            <p>If you voluntarily provide suggestions, ideas, comments, or feedback about the Service, AI Search Advisers may use that feedback to improve its products and services without an obligation to compensate you.</p>

            <h2 id="sec-10">10. Third-Party Services</h2>
            <p>AI Search Advisers may interact with or rely on third-party services, including:</p>
            <ul>
              <li>AI platforms</li>
              <li>Search engines</li>
              <li>Hosting providers</li>
              <li>Payment processors</li>
              <li>Email providers</li>
              <li>Business directories</li>
              <li>Analytics providers</li>
              <li>Other technology platforms</li>
            </ul>
            <p>Your use of third-party services may also be subject to the terms and privacy policies of those providers.</p>
            <p>AI Search Advisers does not control third-party services and is not responsible for their independent actions, outages, availability, pricing, policies, or changes.</p>

            <h2 id="sec-11">11. Confidentiality</h2>
            <p>Each party may receive confidential or non-public information from the other in connection with the Service.</p>
            <p>Each party agrees to:</p>
            <ul>
              <li>Protect such information using reasonable care.</li>
              <li>Use it only as necessary for the relevant business relationship.</li>
              <li>Limit disclosure to personnel, contractors, or professional advisers who reasonably need access and are subject to appropriate confidentiality obligations.</li>
            </ul>
            <p>Confidentiality obligations do not apply to information that:</p>
            <ul>
              <li>Is publicly available through no breach of obligation.</li>
              <li>Was lawfully known before disclosure.</li>
              <li>Is independently developed without use of confidential information.</li>
              <li>Is lawfully received from another source.</li>
              <li>Must be disclosed under applicable law or legal process.</li>
            </ul>

            <h2 id="sec-12">12. Privacy</h2>
            <p>Our handling of personal information is described in the AI Search Advisers Privacy Policy.</p>
            <p>Please review:</p>
            <p><strong>Privacy Policy:</strong> <a href="/privacy-policy">/privacy-policy</a></p>
            <p>The Privacy Policy forms part of the terms governing your use of the Service.</p>
            <p>Where AI Search Advisers processes personal information on behalf of a customer and additional data-processing terms are legally required, appropriate terms may be made available separately.</p>

            <h2 id="sec-13">13. Disclaimers</h2>
            <p>THE SERVICE, REPORTS, ANALYSES, AND RELATED MATERIALS ARE PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW.</p>
            <p>AI Search Advisers DOES NOT GUARANTEE THAT:</p>
            <ul>
              <li>THE SERVICE WILL ALWAYS BE AVAILABLE OR ERROR-FREE.</li>
              <li>REPORTS WILL ALWAYS BE COMPLETE OR ACCURATE.</li>
              <li>THIRD-PARTY AI OR SEARCH SYSTEMS WILL RETURN ANY PARTICULAR RESULT.</li>
              <li>USE OF THE SERVICE WILL PRODUCE A PARTICULAR BUSINESS, SEARCH, VISIBILITY, TRAFFIC, OR REVENUE OUTCOME.</li>
            </ul>
            <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW, AI Search Advisers DISCLAIMS WARRANTIES THAT MAY OTHERWISE BE IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.</p>
            <p>Nothing provided through the Service constitutes legal, financial, tax, accounting, or other regulated professional advice.</p>
            <p>Some jurisdictions may not allow certain warranty exclusions, so portions of this section may not apply to you.</p>

            <h2 id="sec-14">14. Limitation of Liability</h2>
            <p>To the maximum extent permitted by applicable law, AI Search Advisers will not be liable for indirect, incidental, special, consequential, exemplary, or punitive damages arising from or relating to the Service.</p>
            <p>This includes, where legally permitted:</p>
            <ul>
              <li>Lost profits</li>
              <li>Lost revenue</li>
              <li>Lost business opportunities</li>
              <li>Lost data</li>
              <li>Loss of goodwill</li>
              <li>Business interruption</li>
            </ul>
            <p>Where liability cannot legally be excluded, liability will be limited to the maximum extent permitted under applicable law and the applicable agreement between the parties.</p>
            <p>Nothing in these Terms excludes or limits liability that cannot legally be excluded or limited.</p>

            <h2 id="sec-15">15. Indemnification</h2>
            <p>You agree to defend, indemnify, and hold AI Search Advisers harmless from third-party claims, liabilities, damages, losses, and reasonable legal costs arising from:</p>
            <ul>
              <li>Your unlawful or unauthorized use of the Service.</li>
              <li>Your breach of these Terms.</li>
              <li>Your website, content, or data.</li>
              <li>Your lack of authority over a domain submitted to the Service.</li>
              <li>Your violation of applicable law.</li>
              <li>Your infringement of another person's rights.</li>
            </ul>
            <p>Where applicable and subject to the relevant service agreement, AI Search Advisers will address valid third-party claims alleging that proprietary Service materials supplied directly by AI Search Advisers infringe intellectual property rights.</p>
            <p>The parties will reasonably cooperate in the defense of covered claims.</p>

            <h2 id="sec-16">16. Term and Termination</h2>
            <p>These Terms remain in effect for as long as you use the Service.</p>
            <p>You may stop using the Service at any time.</p>
            <p>Paid subscriptions may be cancelled in accordance with the cancellation terms described above.</p>
            <p>AI Search Advisers may suspend or terminate access where reasonably necessary, including for:</p>
            <ul>
              <li>Material breach of these Terms.</li>
              <li>Non-payment.</li>
              <li>Fraud or abuse.</li>
              <li>Security threats.</li>
              <li>Unlawful conduct.</li>
              <li>Conduct that creates material risk to AI Search Advisers, other users, or third parties.</li>
            </ul>
            <p>After termination, your right to access paid or restricted Service functionality may end.</p>
            <p>Where technically and legally appropriate, AI Search Advisers may provide a reasonable opportunity to retrieve account data before it is deleted or anonymized in accordance with applicable retention practices.</p>
            <p>Sections that by their nature should survive termination will continue to apply, including provisions relating to intellectual property, confidentiality, disclaimers, liability, indemnification, and dispute resolution.</p>

            <h2 id="sec-17">17. Governing Law and Dispute Resolution</h2>
            <p>These Terms are governed by applicable laws relating to AI Search Advisers and your use of the Service.</p>
            <p>If a dispute arises, both parties agree to first make a reasonable good-faith effort to resolve the matter informally before beginning formal legal proceedings.</p>
            <p>Nothing in these Terms prevents either party from seeking urgent or injunctive relief where reasonably necessary to protect intellectual property, confidential information, systems, or legal rights.</p>
            <p>Specific venue, jurisdiction, arbitration, or dispute-resolution requirements may also be set out in a separate written agreement where applicable.</p>

            <h2 id="sec-18">18. General</h2>
            
            <h3>Entire Agreement</h3>
            <p>These Terms, together with the Privacy Policy and any applicable written service agreement, proposal, statement of work, or data-processing agreement, form the agreement governing the relevant Service.</p>
            
            <h3>Changes</h3>
            <p>We may update these Terms from time to time.</p>
            <p>The current version will be published on this page with an updated effective or revision date.</p>
            <p>Where required by applicable law, registered users may receive additional notice of material changes.</p>
            
            <h3>Severability</h3>
            <p>If any provision of these Terms is held to be invalid or unenforceable, the remaining provisions will continue in effect to the extent permitted by law.</p>
            
            <h3>No Waiver</h3>
            <p>Failure to enforce a provision of these Terms does not waive the right to enforce it later.</p>
            
            <h3>Assignment</h3>
            <p>You may not assign your rights or obligations under these Terms without our consent where consent is required.</p>
            <p>AI Search Advisers may assign its rights or obligations in connection with a merger, acquisition, restructuring, financing transaction, or sale of relevant business assets.</p>
            
            <h3>Force Majeure</h3>
            <p>Neither party will be responsible for delays or failures caused by circumstances beyond its reasonable control where permitted by law.</p>
            
            <h3>No Partnership</h3>
            <p>These Terms do not create a partnership, employment relationship, joint venture, franchise, or agency relationship between you and AI Search Advisers.</p>
            
            <h3>Publicity</h3>
            <p>AI Search Advisers will not publicly identify you as a customer or use your logo for promotional purposes without appropriate permission.</p>
            
            <h3>Notices</h3>
            <p>Important notices may be delivered through the Service, through the email address associated with your account, or through other reasonable contact methods.</p>

            <h2 id="sec-19">19. Contact</h2>
            <p>If you have questions about these Terms, billing, your subscription, or use of the Service, please contact AI Search Advisers through the official contact or support channels available on our website.</p>
            <p className="mt-8 font-semibold"><a href="mailto:info@aisearchadvisers.com">info@aisearchadvisers.com</a></p>
          </div>
        </div>
      </section>

      <GlobalFooter />
    </div>
  );
}
